import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import MainHeader from '../../components/MainHeader';
import Footer from '../../components/Footer';
import './AnalyticsScreen.css';

export default function AnalyticsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [coupleData, setCoupleData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  async function loadAnalytics() {
    try {
      const promises = [api.get('/analytics/history?limit=10')];
      if (user?.accountType === 'couple') {
        promises.push(api.get('/analytics/couple'));
      }
      const results = await Promise.allSettled(promises);

      if (results[0].status === 'fulfilled') {
        setHistory(results[0].value.data.history || []);
      }
      if (results[1]?.status === 'fulfilled' && results[1].value.data.couple?.hasData) {
        setCoupleData(results[1].value.data.couple);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      setLoading(false);
    }
  }

  function getMatchColor(pct) {
    if (pct >= 80) return '#22c55e';
    if (pct >= 60) return '#FF6347';
    if (pct >= 40) return '#f59e0b';
    return '#ef4444';
  }

  function formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  const stats = user?.stats || {};
  const totalSwipes = stats.totalSwipes || 0;
  const totalDecisions = stats.totalDecisions || 0;
  const cuisineBreakdown = stats.cuisineBreakdown || {};
  const favoriteRestaurant = stats.favoriteRestaurant;

  const topCuisines = Object.entries(cuisineBreakdown)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  return (
    <div className="main-screen">
      <MainHeader title="Analytics" showBack={true} backPath="/home" />

      <main className="screen-main">
        <div className="analytics-container">
          <div className="stats-overview">
            <div className="stat-card">
              <div className="stat-icon">👆</div>
              <div className="stat-info">
                <div className="stat-value">{totalSwipes}</div>
                <div className="stat-label">Total Swipes</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">🎯</div>
              <div className="stat-info">
                <div className="stat-value">{totalDecisions}</div>
                <div className="stat-label">Decisions Made</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">🍽️</div>
              <div className="stat-info">
                <div className="stat-value">{topCuisines.length}</div>
                <div className="stat-label">Cuisines Tried</div>
              </div>
            </div>
          </div>

          {favoriteRestaurant && (
            <div className="favorite-section">
              <h3 className="section-title">🏆 Favorite Restaurant</h3>
              <div className="favorite-card">
                <h4 className="favorite-name">{favoriteRestaurant}</h4>
                <p className="favorite-subtitle">Most frequently chosen</p>
              </div>
            </div>
          )}

          {topCuisines.length > 0 && (
            <div className="cuisines-section">
              <h3 className="section-title">🍜 Top Cuisines</h3>
              <div className="cuisines-list">
                {topCuisines.map(([cuisine, count], index) => (
                  <div key={cuisine} className="cuisine-item">
                    <div className="cuisine-rank">#{index + 1}</div>
                    <div className="cuisine-info">
                      <div className="cuisine-name">{cuisine}</div>
                      <div className="cuisine-bar">
                        <div 
                          className="cuisine-fill" 
                          style={{ width: `${(count / topCuisines[0][1]) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="cuisine-count">{count}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Couple Taste Profile */}
          {coupleData && (
            <>
              <div className="couple-taste-section">
                <h3 className="section-title">💑 Couple Taste Profile</h3>
                <p className="couple-subtitle">{coupleData.userName} & {coupleData.partnerName}</p>

                <div className="taste-match-wrap">
                  <div className="taste-match-circle" style={{ borderColor: getMatchColor(coupleData.tasteMatch) }}>
                    <span className="taste-match-pct" style={{ color: getMatchColor(coupleData.tasteMatch) }}>
                      {coupleData.tasteMatch}%
                    </span>
                    <span className="taste-match-label">Taste Match</span>
                  </div>
                  <p className="taste-match-desc">
                    {coupleData.tasteMatch >= 80 ? 'You two are food soulmates! 🔥' :
                     coupleData.tasteMatch >= 60 ? 'Great taste alignment! 😊' :
                     coupleData.tasteMatch >= 40 ? 'Some overlap, some adventure! 🍽️' :
                     'Opposites attract! Lots to explore together 🌶️'}
                  </p>
                </div>

                <div className="couple-quick-stats">
                  <div className="couple-quick-stat">
                    <span className="couple-quick-value">{coupleData.totalCoupleSessions}</span>
                    <span className="couple-quick-label">Sessions</span>
                  </div>
                  <div className="couple-quick-stat">
                    <span className="couple-quick-value">{coupleData.agreements}</span>
                    <span className="couple-quick-label">Agreed</span>
                  </div>
                  <div className="couple-quick-stat">
                    <span className="couple-quick-value">{coupleData.disagreements}</span>
                    <span className="couple-quick-label">Disagreed</span>
                  </div>
                </div>
              </div>

              {coupleData.bothLoved?.length > 0 && (
                <div className="couple-loved-section">
                  <h3 className="section-title">❤️ You Both Loved</h3>
                  <div className="loved-list">
                    {coupleData.bothLoved.map((r, i) => (
                      <div key={i} className="loved-item">
                        <div className="loved-rank">{i + 1}</div>
                        <div className="loved-info">
                          <h4 className="loved-name">{r.name}</h4>
                          <p className="loved-cuisine">{r.cuisine} {r.rating > 0 ? `• ⭐ ${r.rating.toFixed(1)}` : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="couple-vs-section">
                <h3 className="section-title">🆚 Who's Pickier?</h3>
                <div className="vs-row">
                  <div className="vs-card">
                    <h4 className="vs-name">{coupleData.userName}</h4>
                    <div className="vs-main-stat" style={{ color: '#FF6347' }}>{coupleData.userProfile.pickiness}%</div>
                    <div className="vs-stat-label">Picky</div>
                    <div className="vs-breakdown">
                      <span>❤️ {coupleData.userProfile.loveRate}%</span>
                      <span>👍 {coupleData.userProfile.likeRate}%</span>
                      <span>👎 {coupleData.userProfile.dislikeRate}%</span>
                    </div>
                  </div>
                  <div className="vs-divider">VS</div>
                  <div className="vs-card">
                    <h4 className="vs-name">{coupleData.partnerName}</h4>
                    <div className="vs-main-stat" style={{ color: '#E64980' }}>{coupleData.partnerProfile.pickiness}%</div>
                    <div className="vs-stat-label">Picky</div>
                    <div className="vs-breakdown">
                      <span>❤️ {coupleData.partnerProfile.loveRate}%</span>
                      <span>👍 {coupleData.partnerProfile.likeRate}%</span>
                      <span>👎 {coupleData.partnerProfile.dislikeRate}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {coupleData.commonCuisines?.length > 0 && (
                <div className="couple-cuisines-section">
                  <h3 className="section-title">🍕 Cuisines You Both Love</h3>
                  <div className="cuisine-chips">
                    {coupleData.commonCuisines.map((c, i) => (
                      <span key={i} className="cuisine-chip">{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {coupleData.recentPicks?.length > 0 && (
                <div className="couple-picks-section">
                  <h3 className="section-title">📍 Recent Couple Picks</h3>
                  <div className="history-list">
                    {coupleData.recentPicks.map((pick, i) => (
                      <div key={i} className="history-item">
                        <div className="history-icon">💑</div>
                        <div className="history-info">
                          <h4 className="history-restaurant">{pick.name}</h4>
                          <p className="history-meta">{pick.cuisine}</p>
                        </div>
                        <div className="history-date">{formatDate(pick.date)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="history-section">
            <h3 className="section-title">📜 Recent Decisions</h3>
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading history...</p>
              </div>
            ) : history.length === 0 ? (
              <div className="empty-state">
                <p>No decisions yet. Start a session to see your history!</p>
              </div>
            ) : (
              <div className="history-list">
                {history.map((decision) => (
                  <div key={decision.id} className="history-item">
                    <div className="history-icon">
                      {decision.mode === 'solo' && '🍽️'}
                      {decision.mode === 'couple' && '💑'}
                      {decision.mode === 'group' && '👥'}
                    </div>
                    <div className="history-info">
                      <h4 className="history-restaurant">
                        {decision.restaurant || decision.finalRestaurant?.name || 'Unknown'}
                      </h4>
                      <p className="history-meta">
                        {decision.cuisine || decision.finalRestaurant?.cuisine || 'Restaurant'} • {decision.mode}
                      </p>
                    </div>
                    <div className="history-date">
                      {formatDate(decision.decisionDate || decision.decision_date)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
